import type { ReactNode } from 'react';

interface QuickActionProps {
  /** Icon SVG / element rendered inside the rounded square. */
  icon: ReactNode;
  /** Big title line, e.g. "Send review request". */
  title: string;
  /** Sub line, e.g. "Pick a recent customer · 1 tap". */
  sub: string;
  /** Click handler — wire to navigation or modal trigger. */
  onClick: () => void;
  /**
   * When true, renders the icon tile with the amber attention variant
   * (FEF3C7 bg / B45309 fg) instead of the default ss-blue-light tile.
   */
  attention?: boolean;
}

/**
 * QuickAction — a clickable row used in the Today view's Quick Actions list.
 * Mock parity: .quick-action (white card with rounded icon tile + title/sub
 * stacked + chevron on the right). Acts as a button for a11y.
 */
export function QuickAction({
  icon,
  title,
  sub,
  onClick,
  attention = false,
}: QuickActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ss-press mx-4 mb-3 flex w-[calc(100%-2rem)] items-center gap-3 rounded-ss-md border border-ink-200 dark:border-[#334155] bg-white dark:bg-[#1E293B] px-4 py-3.5 text-left shadow-ss-sm hover:shadow-ss-md active:bg-ink-50 dark:active:bg-[#334155] animate-ss-fade-in"
    >
      <div
        className={
          attention
            ? 'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px]'
            : 'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-ss-blue-light text-ss-blue'
        }
        style={
          attention ? { background: '#FEF3C7', color: '#B45309' } : undefined
        }
        aria-hidden="true"
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-ink-900 dark:text-[#E2E8F0]">{title}</div>
        <div className="mt-0.5 text-xs text-ink-500 dark:text-[#94A3B8]">{sub}</div>
      </div>
      <div className="text-ink-300 dark:text-[#475569]" aria-hidden="true">
        ›
      </div>
    </button>
  );
}
